import React, {
  Fragment,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  HiX,
  HiDownload,
  HiChevronLeft,
  HiChevronRight,
  HiQuestionMarkCircle,
} from "react-icons/hi";
import {
  ActivityLog,
  TrainingLog,
  HeartRateSample,
  PositionSample,
} from "../../types/post";
import overlayLogoUrl from "../../assets/peakOverlayLogo.png";
import transparentPattern from "../../assets/images/transparent-grid.jpg";
import RadialProgress from "./radialProgressDashboard";
import { useSubscription } from "../../providers/SubscriptionProvider";

interface SummaryExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  trainingLog: TrainingLog & { id: string };
  activity?: (ActivityLog & { id: string }) | null;
  peakScore?: number;
}

const EXPORT_WIDTH = 832; // px (2x 416 for retina)
const EXPORT_HEIGHT = 1040; // px (2x 520 for retina, maintains aspect ratio)

// Utility to generate sparkline path (same logic as Sparkline component)
const generateSparklinePath = (
  values: number[],
  width: number,
  height: number,
): string => {
  if (!values || values.length === 0) return "";
  const finiteValues = values.filter((v) => Number.isFinite(v));
  if (finiteValues.length === 0) return "";

  const min = Math.min(...finiteValues);
  const max = Math.max(...finiteValues);
  const range = max - min || 1;
  const stepX = width / Math.max(values.length - 1, 1);

  const points = values.map((v, i) => {
    const yNorm = (v - min) / range; // 0..1
    const y = height - yNorm * height; // invert for SVG
    const x = i * stepX;
    return [x, y] as const;
  });

  return points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(" ");
};

const clampArray = (values: number[], maxLen: number): number[] => {
  if (values.length <= maxLen) return values;
  const stride = Math.ceil(values.length / maxLen);
  return values.filter((_, i) => i % stride === 0);
};

// GPS Route utilities
const generateRoutePath = (
  positions: PositionSample[],
  width: number,
  height: number,
): string => {
  if (!positions || positions.length === 0) return "";

  const validPositions = positions.filter(
    (p) =>
      p.coords_lat_lng_deg &&
      p.coords_lat_lng_deg.length === 2 &&
      !isNaN(p.coords_lat_lng_deg[0]) &&
      !isNaN(p.coords_lat_lng_deg[1]),
  );

  if (validPositions.length === 0) return "";

  const lats = validPositions.map((p) => p.coords_lat_lng_deg[0]);
  const lngs = validPositions.map((p) => p.coords_lat_lng_deg[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  const latRange = maxLat - minLat || 0.001;
  const lngRange = maxLng - minLng || 0.001;
  const padding = 0.1;
  const paddedMinLat = minLat - latRange * padding;
  const paddedMaxLat = maxLat + latRange * padding;
  const paddedMinLng = minLng - lngRange * padding;
  const paddedMaxLng = maxLng + lngRange * padding;

  const avgLat = (minLat + maxLat) / 2;
  const latMetersPerDegree = 111000;
  const lngMetersPerDegree = 111000 * Math.cos((avgLat * Math.PI) / 180);

  const latRangeMeters = (paddedMaxLat - paddedMinLat) * latMetersPerDegree;
  const lngRangeMeters = (paddedMaxLng - paddedMinLng) * lngMetersPerDegree;

  const gpsAspectRatio = lngRangeMeters / latRangeMeters;
  const containerAspectRatio = width / height;

  let renderWidth = width;
  let renderHeight = height;
  let offsetX = 0;
  let offsetY = 0;

  if (gpsAspectRatio > containerAspectRatio) {
    renderHeight = width / gpsAspectRatio;
    offsetY = (height - renderHeight) / 2;
  } else {
    renderWidth = height * gpsAspectRatio;
    offsetX = (width - renderWidth) / 2;
  }

  const points = validPositions.map((position) => {
    const x =
      offsetX +
      ((position.coords_lat_lng_deg[1] - paddedMinLng) /
        (paddedMaxLng - paddedMinLng)) *
        renderWidth;
    const y =
      offsetY +
      ((paddedMaxLat - position.coords_lat_lng_deg[0]) /
        (paddedMaxLat - paddedMinLat)) *
        renderHeight;
    return [x, y] as const;
  });

  return points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(" ");
};

const clampPositionArray = (
  values: PositionSample[],
  maxLen: number,
): PositionSample[] => {
  if (values.length <= maxLen) return values;
  const stride = Math.ceil(values.length / maxLen);
  return values.filter((_, i) => i % stride === 0);
};

const formatMinutes = (minutes: number): string => {
  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}:${String(mins).padStart(2, "0")}`;
};

// Overlay type: 1-5 are non-GPS, 6-11 are GPS-based, 9 is Load+Time only
type OverlayId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

const SummaryExportDialog: React.FC<SummaryExportDialogProps> = ({
  isOpen,
  onClose,
  trainingLog,
  activity,
  peakScore = 0,
}) => {
  const { isPlusOrHigher } = useSubscription();
  const [isExporting, setIsExporting] = useState(false);
  const [selectedOverlay, setSelectedOverlay] = useState<OverlayId>(1);

  // Hide peak score overlays (1, 2, 4, 5) when score is 0 or user is on free plan
  const showPeakScoreOverlays = peakScore > 0 && isPlusOrHigher;
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [calSansFontData, setCalSansFontData] = useState<string | null>(null);
  const [geistMonoFontData, setGeistMonoFontData] = useState<string | null>(
    null,
  );

  // Load and inline the SVG logo, Cal Sans font, and Geist Mono font
  useEffect(() => {
    let cancelled = false;

    const loadFontAsDataUrl = async (
      url: string,
      name: string,
    ): Promise<string | null> => {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        const dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        try {
          const fontFace = new FontFace(name, `url(${dataUrl})`);
          await fontFace.load();
          document.fonts.add(fontFace);
        } catch (preloadError) {
          console.warn(`${name} font preload failed`, preloadError);
        }
        return dataUrl;
      } catch (e) {
        console.warn(`Failed to load ${name} font`, e);
        return null;
      }
    };

    const loadAssets = async () => {
      try {
        // Load logo PNG as data URL for SVG embedding
        const logoRes = await fetch(overlayLogoUrl);
        const logoBlob = await logoRes.blob();
        const logoData = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(logoBlob);
        });
        if (cancelled) return;
        setLogoDataUrl(logoData);

        if (cancelled) return;

        // Load fonts in parallel
        const [calSansData, geistMonoData] = await Promise.all([
          loadFontAsDataUrl(
            "https://fonts.gstatic.com/s/calsans/v2/rP2Hp2-T6wTYnj8AYF_k5t_xLzBTMU6rl3CzCnA.woff2",
            "Cal Sans",
          ),
          loadFontAsDataUrl(
            "https://fonts.gstatic.com/s/geistmono/v5/or3yQ6H-1_WfwkMZI_qYPLs1a-t7PU0AbeHaL65U5Cl4PuCTfNg.woff2",
            "Geist Mono",
          ),
        ]);

        if (!cancelled) {
          setCalSansFontData(calSansData);
          setGeistMonoFontData(geistMonoData);
        }
      } catch (e) {
        console.warn("Failed to load assets", e);
        setLogoDataUrl(null);
      }
    };

    loadAssets();

    return () => {
      cancelled = true;
    };
  }, []);

  // Extract HR samples for sparkline if available
  const heartRateValues = useMemo(() => {
    const samples: HeartRateSample[] =
      (activity?.heartRateData?.detailed?.hr_samples as
        | HeartRateSample[]
        | undefined) || [];
    const values = samples
      .map((s) => Number(s?.bpm))
      .filter((v) => Number.isFinite(v) && v > 0) as number[];
    if (values.length > 0) return clampArray(values, 180);
    return [] as number[];
  }, [activity?.heartRateData?.detailed?.hr_samples]);

  // Extract GPS position data and calculate distance
  const { positionValues, distance, hasGPSData, avgHR, maxHR, hasHRData } =
    useMemo(() => {
      const samples: PositionSample[] =
        activity?.positionData?.position_samples || [];
      const validSamples = samples.filter(
        (s) =>
          s.coords_lat_lng_deg &&
          s.coords_lat_lng_deg.length === 2 &&
          !isNaN(s.coords_lat_lng_deg[0]) &&
          !isNaN(s.coords_lat_lng_deg[1]),
      );

      const clampedSamples =
        validSamples.length > 200
          ? clampPositionArray(validSamples, 200)
          : validSamples;

      // Calculate distance using Haversine formula
      let totalDistance = 0;
      if (clampedSamples.length >= 2) {
        for (let i = 1; i < clampedSamples.length; i++) {
          const [lat1, lng1] = clampedSamples[i - 1].coords_lat_lng_deg;
          const [lat2, lng2] = clampedSamples[i].coords_lat_lng_deg;

          const R = 6371000;
          const dLat = ((lat2 - lat1) * Math.PI) / 180;
          const dLng = ((lng2 - lng1) * Math.PI) / 180;
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * Math.PI) / 180) *
              Math.cos((lat2 * Math.PI) / 180) *
              Math.sin(dLng / 2) *
              Math.sin(dLng / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          totalDistance += R * c;
        }
      }

      const distanceKm = totalDistance / 1000;
      const avgHeartRate = activity?.aveHR || null;
      const maxHeartRate = activity?.maxHR || null;
      const hasHeartRateData = avgHeartRate !== null && maxHeartRate !== null;

      return {
        positionValues: clampedSamples,
        distance: distanceKm,
        hasGPSData: clampedSamples.length > 0,
        avgHR: avgHeartRate,
        maxHR: maxHeartRate,
        hasHRData: hasHeartRateData,
      };
    }, [
      activity?.positionData?.position_samples,
      activity?.aveHR,
      activity?.maxHR,
    ]);

  // Auto-select appropriate overlay based on data availability
  useEffect(() => {
    // If we have GPS data and currently on non-HR overlay without GPS, stay there
    // If no HR data and currently on HR-requiring overlay (1, 2, or 3), switch
    if (
      heartRateValues.length === 0 &&
      !hasGPSData &&
      (selectedOverlay === 1 || selectedOverlay === 2 || selectedOverlay === 3)
    ) {
      setSelectedOverlay(4);
    }
    // If on GPS overlay but no GPS data, switch to non-GPS overlay
    if (
      !hasGPSData &&
      (selectedOverlay === 6 ||
        selectedOverlay === 7 ||
        selectedOverlay === 8 ||
        selectedOverlay === 10 ||
        selectedOverlay === 11)
    ) {
      setSelectedOverlay(heartRateValues.length > 0 ? 1 : 4);
    }
  }, [heartRateValues.length, hasGPSData, selectedOverlay]);

  const buildOverlay1SVG = useCallback((): string => {
    const centerX = EXPORT_WIDTH / 2;
    const centerY = EXPORT_HEIGHT / 2;

    // Scale factor for better readability (1.5x larger than preview)
    const scaleFactor = 1.5;

    // Define consistent spacing - minimized HR value to label spacing
    const spacing = {
      section: 50 * scaleFactor, // Between major sections (increased from 40)
      element: 12 * scaleFactor, // Between related elements
      textOffset: 20 * scaleFactor, // For visual text centering
      loadValueToLabel: 4 * scaleFactor, // Very tight spacing for Load value to label
      hrLabelToValue: 8 * scaleFactor, // Minimized spacing between HR value and label (decreased from 20)
    };

    // Logo dimensions (scaled)
    const logoWidth = 240;
    const logoHeight = logoWidth * (44.8 / 112);

    // Radial progress dimensions
    const peakPercentage = Math.min((peakScore / 110) * 100, 100);
    const radius = 100;
    const strokeWidth = 14;
    const circumference = 2 * Math.PI * radius;

    // Text sizes (scaled)
    const valueSize = 96;
    const labelSize = 32;

    // Calculate sparkline dimensions
    const hasSparkline = heartRateValues.length > 0 && activity;
    const sparklineWidth = hasSparkline ? 400 * scaleFactor : 0;
    const sparklineHeight = hasSparkline ? 100 * scaleFactor : 0;

    // Calculate total content height INCLUDING all elements
    let totalContentHeight = radius * 2 + spacing.element + labelSize; // Peak circle + label
    totalContentHeight += spacing.section; // Space to Load
    totalContentHeight += valueSize + spacing.loadValueToLabel + labelSize; // Load value + tight spacing + label

    // Add Average HR if present
    if (activity && activity.aveHR) {
      totalContentHeight += spacing.section; // Space to Ave HR
      totalContentHeight += labelSize + spacing.hrLabelToValue + valueSize; // Ave HR label + generous spacing + value
    }

    // Add sparkline if present
    if (hasSparkline) {
      totalContentHeight += spacing.section; // Space to sparkline
      totalContentHeight += sparklineHeight; // Sparkline height
    }

    totalContentHeight += spacing.section; // Space before logo
    totalContentHeight += logoHeight; // Logo

    // Center the entire content block
    const contentStartY = centerY - totalContentHeight / 2;

    // Calculate Y positions systematically
    let currentY = contentStartY;

    const peakScoreCenterY = currentY + radius;
    currentY += radius * 2 + spacing.element + labelSize + spacing.section;

    const loadValueY = currentY + spacing.textOffset;
    const loadLabelY = currentY + valueSize + spacing.loadValueToLabel;
    currentY += valueSize + spacing.loadValueToLabel + labelSize;

    let aveHRLabelY = 0;
    let aveHRValueY = 0;
    if (activity && activity.aveHR) {
      currentY += spacing.section;
      aveHRLabelY = currentY;
      aveHRValueY =
        currentY + labelSize + spacing.hrLabelToValue + spacing.textOffset;
      currentY += labelSize + spacing.hrLabelToValue + valueSize;
    }

    let sparklineY = 0;
    if (hasSparkline) {
      currentY += spacing.section;
      sparklineY = currentY;
      currentY += sparklineHeight;
    }

    currentY += spacing.section;
    const logoY = currentY;

    // Build layout with systematic positioning
    let content = "";

    // Peak Score with radial progress
    content += `
      <!-- Peak Score Radial Progress -->
      <g transform="translate(${centerX}, ${peakScoreCenterY})">
        <!-- Background circle -->
        <circle cx="0" cy="0" r="${radius}" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="${strokeWidth}"/>
        <!-- Progress circle -->
        <circle cx="0" cy="0" r="${radius}" fill="none" stroke="#ffffff" stroke-width="${strokeWidth}"
                stroke-dasharray="${
                  circumference * (peakPercentage / 100)
                } ${circumference}"
                transform="rotate(-90)" stroke-linecap="round"/>
        <!-- Peak Score Value - properly centered using dominant-baseline -->
        <text class="peak-value" x="0" y="0" text-anchor="middle" dominant-baseline="central">${Math.round(
          peakScore,
        )}</text>
        <!-- Peak Score Label -->
        <text class="peak-label" x="0" y="${
          radius + spacing.element + labelSize * 0.3
        }" text-anchor="middle" dominant-baseline="hanging">Peak Readiness</text>
      </g>
    `;

    // Load section
    content += `
      <text class="value" x="${centerX}" y="${loadValueY}" text-anchor="middle" dominant-baseline="central">${Math.round(
        trainingLog.load || 0,
      )}</text>
      <text class="label" x="${centerX}" y="${loadLabelY}" text-anchor="middle" dominant-baseline="hanging">Load</text>
    `;

    // Average Heart Rate (if available)
    if (activity && activity.aveHR) {
      content += `
        <text class="value" x="${centerX}" y="${
          aveHRLabelY + labelSize * 0.3
        }" text-anchor="middle" dominant-baseline="central">${Math.round(
          activity.aveHR,
        )}</text>
        <text class="label" x="${centerX}" y="${aveHRValueY}" text-anchor="middle" dominant-baseline="hanging">Average HR</text>
      `;
    }

    // Heart Rate Sparkline (if available)
    if (hasSparkline) {
      const sparklinePath = generateSparklinePath(
        heartRateValues,
        sparklineWidth,
        sparklineHeight,
      );

      content += `
        <!-- Heart Rate Sparkline -->
        <g transform="translate(${
          centerX - sparklineWidth / 2
        }, ${sparklineY})">
          <path d="${sparklinePath}" stroke="#FFFFFF" stroke-width="${
            3 * scaleFactor
          }" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </g>
      `;
    }

    // PEAK Logo at the bottom
    content += `
      <g transform="translate(${centerX - logoWidth / 2}, ${logoY})">
        ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
      </g>
    `;

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${EXPORT_WIDTH}" height="${EXPORT_HEIGHT}" viewBox="0 0 ${EXPORT_WIDTH} ${EXPORT_HEIGHT}">
  <defs>
    <style type="text/css">
      .label {
        font-family: 'Funnel Sans', -apple-system, system-ui, sans-serif;
        font-size: ${labelSize}px;
        fill: rgba(255, 255, 255, 0.7);
        font-weight: 400;
      }
      .peak-label {
        font-family: 'Funnel Sans', -apple-system, system-ui, sans-serif;
        font-size: ${labelSize}px;
        fill: rgba(255, 255, 255, 0.7);
        font-weight: 400;
      }
      .value {
        font-family: 'Funnel Sans', -apple-system, system-ui, sans-serif;
        font-size: ${valueSize}px;
        fill: #FFFFFF;
        font-weight: 700;
      }
      .peak-value {
        font-family: 'Funnel Sans', -apple-system, system-ui, sans-serif;
        font-size: ${valueSize}px;
        fill: #FFFFFF;
        font-weight: 700;
      }
    </style>
  </defs>

  <!-- Content -->
  ${content}
</svg>`;
  }, [trainingLog, logoDataUrl, peakScore, activity, heartRateValues]);

  const buildOverlay2SVG = useCallback((): string => {
    // Calculate dynamic height for Overlay 2 (has more content than Overlay 1)
    const OVERLAY2_WIDTH = EXPORT_WIDTH;

    // Scale factor for better readability (1.5x larger than preview)
    const scaleFactor = 1.5;

    // Define consistent spacing - equal spacing between all metrics
    const spacing = {
      section: 50 * scaleFactor, // Equal spacing between all sections
      element: 12 * scaleFactor, // Between related elements
      textOffset: 20 * scaleFactor, // For visual text centering
      valueToLabel: 4 * scaleFactor, // Tight spacing from value to label
      topPadding: 60 * scaleFactor, // Top padding
      bottomPadding: 60 * scaleFactor, // Bottom padding
    };

    // Logo dimensions (scaled)
    const logoWidth = 240;
    const logoHeight = logoWidth * (44.8 / 112);

    // Text sizes (scaled)
    const valueSize = 96;
    const labelSize = 32;

    // Calculate sparkline dimensions
    const hasSparkline = heartRateValues.length > 0 && activity;
    const sparklineWidth = hasSparkline ? 400 * scaleFactor : 0;
    const sparklineHeight = hasSparkline ? 100 * scaleFactor : 0;

    // Calculate total content height INCLUDING all elements
    let totalContentHeight = spacing.topPadding; // Top padding
    totalContentHeight += valueSize + spacing.valueToLabel + labelSize; // Load

    // Add Average HR if present
    if (activity && activity.aveHR) {
      totalContentHeight += spacing.section; // Space to Ave HR
      totalContentHeight += valueSize + spacing.valueToLabel + labelSize; // Ave HR
    }

    // Add Max HR if present
    if (activity && activity.maxHR) {
      totalContentHeight += spacing.section; // Space to Max HR
      totalContentHeight += valueSize + spacing.valueToLabel + labelSize; // Max HR
    }

    // Add sparkline if present
    if (hasSparkline) {
      totalContentHeight += spacing.section; // Space to sparkline
      totalContentHeight += sparklineHeight; // Sparkline height
    }

    totalContentHeight += spacing.section; // Space before logo
    totalContentHeight += logoHeight; // Logo
    totalContentHeight += spacing.bottomPadding; // Bottom padding

    // Use dynamic height for Overlay 2
    const OVERLAY2_HEIGHT = totalContentHeight;
    const centerX = OVERLAY2_WIDTH / 2;

    // Calculate Y positions systematically (no vertical centering, just flow from top)
    let currentY = spacing.topPadding;

    // Load
    const loadValueY = currentY + spacing.textOffset;
    const loadLabelY = currentY + valueSize + spacing.valueToLabel;
    currentY += valueSize + spacing.valueToLabel + labelSize;

    // Average HR
    let aveHRValueY = 0;
    let aveHRLabelY = 0;
    if (activity && activity.aveHR) {
      currentY += spacing.section;
      aveHRValueY = currentY + spacing.textOffset;
      aveHRLabelY = currentY + valueSize + spacing.valueToLabel;
      currentY += valueSize + spacing.valueToLabel + labelSize;
    }

    // Max HR
    let maxHRValueY = 0;
    let maxHRLabelY = 0;
    if (activity && activity.maxHR) {
      currentY += spacing.section;
      maxHRValueY = currentY + spacing.textOffset;
      maxHRLabelY = currentY + valueSize + spacing.valueToLabel;
      currentY += valueSize + spacing.valueToLabel + labelSize;
    }

    // Sparkline
    let sparklineY = 0;
    if (hasSparkline) {
      currentY += spacing.section;
      sparklineY = currentY;
      currentY += sparklineHeight;
    }

    currentY += spacing.section;
    const logoY = currentY;

    // Build layout with systematic positioning
    let content = "";

    // Load section
    content += `
      <text class="value" x="${centerX}" y="${loadValueY}" text-anchor="middle" dominant-baseline="central">${Math.round(
        trainingLog.load || 0,
      )}</text>
      <text class="label" x="${centerX}" y="${loadLabelY}" text-anchor="middle" dominant-baseline="hanging">Load</text>
    `;

    // Average Heart Rate (if available)
    if (activity && activity.aveHR) {
      content += `
        <text class="value" x="${centerX}" y="${aveHRValueY}" text-anchor="middle" dominant-baseline="central">${Math.round(
          activity.aveHR,
        )}</text>
        <text class="label" x="${centerX}" y="${aveHRLabelY}" text-anchor="middle" dominant-baseline="hanging">Average HR</text>
      `;
    }

    // Max Heart Rate (if available)
    if (activity && activity.maxHR) {
      content += `
        <text class="value" x="${centerX}" y="${maxHRValueY}" text-anchor="middle" dominant-baseline="central">${Math.round(
          activity.maxHR,
        )}</text>
        <text class="label" x="${centerX}" y="${maxHRLabelY}" text-anchor="middle" dominant-baseline="hanging">Max HR</text>
      `;
    }

    // Heart Rate Sparkline (if available)
    if (hasSparkline) {
      const sparklinePath = generateSparklinePath(
        heartRateValues,
        sparklineWidth,
        sparklineHeight,
      );

      content += `
        <!-- Heart Rate Sparkline -->
        <g transform="translate(${
          centerX - sparklineWidth / 2
        }, ${sparklineY})">
          <path d="${sparklinePath}" stroke="#FFFFFF" stroke-width="${
            3 * scaleFactor
          }" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </g>
      `;
    }

    // PEAK Logo at the bottom
    content += `
      <g transform="translate(${centerX - logoWidth / 2}, ${logoY})">
        ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
      </g>
    `;

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${OVERLAY2_WIDTH}" height="${OVERLAY2_HEIGHT}" viewBox="0 0 ${OVERLAY2_WIDTH} ${OVERLAY2_HEIGHT}">
  <defs>
    <style type="text/css">
      .label {
        font-family: 'Funnel Sans', -apple-system, system-ui, sans-serif;
        font-size: ${labelSize}px;
        fill: rgba(255, 255, 255, 0.7);
        font-weight: 400;
      }
      .value {
        font-family: 'Funnel Sans', -apple-system, system-ui, sans-serif;
        font-size: ${valueSize}px;
        fill: #FFFFFF;
        font-weight: 700;
      }
    </style>
  </defs>

  <!-- Content -->
  ${content}
</svg>`;
  }, [trainingLog, logoDataUrl, activity, heartRateValues]);

  // Build Overlay 3 - Grid layout with Load, Time, Avg HR, Max HR
  const buildOverlay3SVG = useCallback((): string => {
    const OVERLAY3_WIDTH = 832;

    // Layout configuration
    const padding = { top: 80, bottom: 80, left: 140, right: 140 };
    const centerX = OVERLAY3_WIDTH / 2;

    // Logo dimensions (scaled to match larger PEAK header)
    const logoWidth = 240; // Increased from 180 for more prominent header
    const logoHeight = logoWidth * (44.8 / 112); // Maintain aspect ratio

    // Spacing - increased for better breathing room
    const spacing = {
      header: 180, // Space after PEAK header (increased significantly for more gap)
      element: 60, // Space between rows
      chart: 80, // Space before chart
    };

    // Font sizes
    const headerSize = 64; // Kept for spacing calculations
    const labelSize = 32;
    const valueSize = 96;

    // Calculate grid dimensions
    const gridStartY = padding.top + 60; // Start content near top with some padding
    const columnWidth = (OVERLAY3_WIDTH - padding.left - padding.right) / 2;
    const leftColX = padding.left + columnWidth / 2;
    const rightColX = padding.left + columnWidth + columnWidth / 2;

    // Row positions
    const row1ValueY = gridStartY + valueSize / 2;
    const row1LabelY = row1ValueY + valueSize / 2 + spacing.element / 2;
    const row2ValueY = row1LabelY + spacing.element + valueSize / 2;
    const row2LabelY = row2ValueY + valueSize / 2 + spacing.element / 2;

    // Chart positioning
    const chartY = row2LabelY + spacing.chart;
    const chartHeight = 120;
    const chartWidth = OVERLAY3_WIDTH - padding.left - padding.right;

    // Logo at bottom
    const logoBottomY = chartY + chartHeight + spacing.chart;

    // Calculate total height
    const OVERLAY3_HEIGHT = logoBottomY + logoHeight + padding.bottom;

    // Get duration from training log (duration is in minutes)
    const timeValue = trainingLog.duration
      ? formatMinutes(trainingLog.duration)
      : "-";

    // Build content
    let content = `
      <!-- Row 1: Load and Time -->
      <!-- Load (Left) -->
      <text class="value" x="${leftColX}" y="${row1ValueY}" 
            text-anchor="middle" dominant-baseline="central">${Math.round(trainingLog.load || 0)}</text>
      <text class="label" x="${leftColX}" y="${row1LabelY}" 
            text-anchor="middle" dominant-baseline="hanging">Load</text>
      
      <!-- Time (Right) -->
      <text class="value" x="${rightColX}" y="${row1ValueY}" 
            text-anchor="middle" dominant-baseline="central">${timeValue}</text>
      <text class="label" x="${rightColX}" y="${row1LabelY}" 
            text-anchor="middle" dominant-baseline="hanging">Duration</text>
      
      <!-- Row 2: Average HR and Max HR -->
    `;

    // Average HR (Left) - only if available
    if (activity && activity.aveHR) {
      content += `
      <!-- Average HR (Left) -->
      <text class="value" x="${leftColX}" y="${row2ValueY}" 
            text-anchor="middle" dominant-baseline="central">${Math.round(activity.aveHR)}</text>
      <text class="label" x="${leftColX}" y="${row2LabelY}" 
            text-anchor="middle" dominant-baseline="hanging">Average HR</text>
      `;
    }

    // Max HR (Right) - only if available
    if (activity && activity.maxHR) {
      content += `
      <!-- Max HR (Right) -->
      <text class="value" x="${rightColX}" y="${row2ValueY}" 
            text-anchor="middle" dominant-baseline="central">${Math.round(activity.maxHR)}</text>
      <text class="label" x="${rightColX}" y="${row2LabelY}" 
            text-anchor="middle" dominant-baseline="hanging">Max HR</text>
      `;
    }

    // Heart rate chart (if available)
    if (activity && heartRateValues.length > 0) {
      const sparklinePath = generateSparklinePath(
        heartRateValues,
        chartWidth,
        chartHeight,
      );
      content += `
      <!-- Heart Rate Chart -->
      <g transform="translate(${padding.left}, ${chartY})">
        <path d="${sparklinePath}" stroke="#FFFFFF" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      `;
    }

    // PEAK Logo at the bottom
    content += `
      <g transform="translate(${centerX - logoWidth / 2}, ${logoBottomY})">
        ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
      </g>
    `;

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${OVERLAY3_WIDTH}" height="${OVERLAY3_HEIGHT}" viewBox="0 0 ${OVERLAY3_WIDTH} ${OVERLAY3_HEIGHT}">
  <defs>
    <style>
      text {
        fill: white;
      }
      .header {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${headerSize}px;
        font-weight: 700;
        letter-spacing: 0.05em;
      }
      .value {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${valueSize}px;
        font-weight: 700;
      }
      .label {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${labelSize}px;
        font-weight: 400;
        fill: rgba(255, 255, 255, 0.7);
      }
    </style>
  </defs>

  <!-- Content -->
  ${content}
</svg>`;
  }, [trainingLog, logoDataUrl, activity, heartRateValues]);

  // Build Overlay 4 - Simple radial progress with Peak Readiness text
  const buildOverlay4SVG = useCallback((): string => {
    const OVERLAY4_WIDTH = 832;
    const OVERLAY4_HEIGHT = 832; // Square format for centered radial

    const centerX = OVERLAY4_WIDTH / 2;
    const centerY = OVERLAY4_HEIGHT / 2;

    // Radial progress dimensions - larger for prominence
    const peakPercentage = Math.min((peakScore / 110) * 100, 100);
    const radius = 160; // Much larger radius for focal point
    const strokeWidth = 20; // Thicker stroke
    const circumference = 2 * Math.PI * radius;

    // Text sizes
    const scoreSize = 140; // Large score number
    const labelSize = 32; // "Peak" and "Readiness" text
    const labelSpacing = 8; // Space between "Peak" and "Readiness"

    // Calculate label positions below the circle
    const labelY = centerY + radius + 60; // Position below the circle

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${OVERLAY4_WIDTH}" height="${OVERLAY4_HEIGHT}" viewBox="0 0 ${OVERLAY4_WIDTH} ${OVERLAY4_HEIGHT}">
  <defs>
    <style type="text/css">
      .score-value {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${scoreSize}px;
        fill: #FFFFFF;
        font-weight: 700;
      }
      .readiness-label {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${labelSize}px;
        fill: #FFFFFF;
        font-weight: 600;
      }
    </style>
  </defs>

  <!-- Radial Progress Circle -->
  <g transform="translate(${centerX}, ${centerY})">
    <!-- Background circle -->
    <circle cx="0" cy="0" r="${radius}" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="${strokeWidth}"/>
    
    <!-- Progress circle -->
    <circle cx="0" cy="0" r="${radius}" fill="none" stroke="#ffffff" stroke-width="${strokeWidth}"
            stroke-dasharray="${circumference * (peakPercentage / 100)} ${circumference}"
            transform="rotate(-90)" stroke-linecap="round"/>
    
    <!-- Peak Score Value - centered in circle -->
    <text class="score-value" x="0" y="0" text-anchor="middle" dominant-baseline="central">${Math.round(peakScore)}</text>
  </g>
  
  <!-- Peak Readiness Label - below circle -->
  <text class="readiness-label" x="${centerX}" y="${labelY}" text-anchor="middle" dominant-baseline="hanging">Peak</text>
  <text class="readiness-label" x="${centerX}" y="${labelY + labelSize + labelSpacing}" text-anchor="middle" dominant-baseline="hanging">Readiness</text>
</svg>`;
  }, [peakScore]);

  // Build Overlay 5 - Radial progress with Peak Score + Load & Time grid
  const buildOverlay5SVG = useCallback((): string => {
    const OVERLAY5_WIDTH = 832;
    const OVERLAY5_HEIGHT = 1040; // Portrait format

    const centerX = OVERLAY5_WIDTH / 2;

    // Layout configuration
    const padding = { top: 80, left: 60, right: 60 };

    // Logo dimensions
    const logoWidth = 240;
    const logoHeight = logoWidth * (44.8 / 112);

    // Radial progress dimensions - smaller than Overlay 4
    const peakPercentage = Math.min((peakScore / 110) * 100, 100);
    const radius = 120; // Smaller than Overlay 4's 160px
    const strokeWidth = 16;
    const circumference = 2 * Math.PI * radius;

    // Font sizes
    const scoreSize = 100; // Score inside circle
    const peakScoreLabelSize = 28; // "Peak Score" text
    const gridValueSize = 96; // Bottom metrics values
    const gridLabelSize = 32; // Bottom metrics labels

    // Spacing
    const radialToLabel = 40;
    const labelToGrid = 80;

    // Calculate positions (content starts from top, logo at bottom)
    const radialY = padding.top + radius;
    const peakScoreLabelY = radialY + radius + radialToLabel;
    const gridStartY = peakScoreLabelY + peakScoreLabelSize + labelToGrid;

    // Grid dimensions
    const gridItemHeight = 180;
    const columnWidth = (OVERLAY5_WIDTH - padding.left - padding.right) / 2;
    const leftColX = padding.left + columnWidth / 2;
    const rightColX = padding.left + columnWidth + columnWidth / 2;

    // Grid item positions (centered vertically)
    const gridValueY = gridStartY + gridItemHeight / 2;
    const gridLabelY = gridValueY + gridValueSize / 2 + 30;

    // Logo at the bottom
    const logoY = gridStartY + gridItemHeight + 60;

    // Get duration from training log (duration is in minutes)
    const timeValue = trainingLog.duration
      ? formatMinutes(trainingLog.duration)
      : "-";

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${OVERLAY5_WIDTH}" height="${OVERLAY5_HEIGHT}" viewBox="0 0 ${OVERLAY5_WIDTH} ${OVERLAY5_HEIGHT}">
  <defs>
    <style type="text/css">
      .score-value-5 {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${scoreSize}px;
        fill: #FFFFFF;
        font-weight: 700;
      }
      .peak-score-label {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${peakScoreLabelSize}px;
        fill: rgba(255, 255, 255, 0.7);
        font-weight: 400;
      }
      .grid-value-5 {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${gridValueSize}px;
        fill: #FFFFFF;
        font-weight: 700;
      }
      .grid-label-5 {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${gridLabelSize}px;
        fill: rgba(255, 255, 255, 0.7);
        font-weight: 400;
      }
    </style>
  </defs>

  <!-- Radial Progress Circle -->
  <g transform="translate(${centerX}, ${radialY})">
    <!-- Background circle -->
    <circle cx="0" cy="0" r="${radius}" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="${strokeWidth}"/>
    
    <!-- Progress circle -->
    <circle cx="0" cy="0" r="${radius}" fill="none" stroke="#ffffff" stroke-width="${strokeWidth}"
            stroke-dasharray="${circumference * (peakPercentage / 100)} ${circumference}"
            transform="rotate(-90)" stroke-linecap="round"/>

    <!-- Peak Score Value - centered in circle -->
    <text class="score-value-5" x="0" y="0" text-anchor="middle" dominant-baseline="central">${Math.round(peakScore)}</text>
  </g>
  
  <!-- Peak Score Label - below circle -->
  <text class="peak-score-label" x="${centerX}" y="${peakScoreLabelY}" text-anchor="middle" dominant-baseline="hanging">Peak Readiness</text>
  
  <!-- Bottom Grid: Load and Time -->
  <!-- Load (Left) -->
  <text class="grid-value-5" x="${leftColX}" y="${gridValueY}" text-anchor="middle" dominant-baseline="central">${Math.round(trainingLog.load || 0)}</text>
  <text class="grid-label-5" x="${leftColX}" y="${gridLabelY}" text-anchor="middle" dominant-baseline="hanging">Load</text>
  
  <!-- Duration (Right) -->
  <text class="grid-value-5" x="${rightColX}" y="${gridValueY}" text-anchor="middle" dominant-baseline="central">${timeValue}</text>
  <text class="grid-label-5" x="${rightColX}" y="${gridLabelY}" text-anchor="middle" dominant-baseline="hanging">Duration</text>

  <!-- PEAK Logo - bottom -->
  <g transform="translate(${centerX - logoWidth / 2}, ${logoY})">
    ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
  </g>
</svg>`;
  }, [peakScore, trainingLog, logoDataUrl]);

  // Build Overlay 9 - Simple Load + Time only
  const buildOverlay9SVG = useCallback((): string => {
    const OV_WIDTH = 832;
    const OV_HEIGHT = 832; // Square format

    const centerX = OV_WIDTH / 2;

    // Layout configuration
    const padding = { top: 80, left: 60, right: 60 };

    // Logo dimensions
    const logoWidth = 240;
    const logoHeight = logoWidth * (44.8 / 112);

    // Font sizes
    const valueSize = 96;
    const labelSize = 32;
    const tagSize = 22;

    // Calculate positions (content starts from top)
    const gridStartY = padding.top;

    // Grid dimensions - side by side
    const columnWidth = (OV_WIDTH - padding.left - padding.right) / 2;
    const leftColX = padding.left + columnWidth / 2;
    const rightColX = padding.left + columnWidth + columnWidth / 2;

    // Vertically center in remaining space
    const gridValueY = gridStartY + 60;
    const gridLabelY = gridValueY + valueSize / 2 + 30;

    // Tags position
    const tags =
      trainingLog.tag && trainingLog.tag.length > 0 ? trainingLog.tag : [];
    const tagPadY = 16;
    const tagPadX = 40;
    const tagRowGap = 12;
    const tagHeightVal = tagSize + tagPadY * 2;
    const tagCharWidth = tagSize * 0.62; // monospace char width approximation
    const tagGap = tags.length > 0 ? Math.max(30, 60 - tags.length * 8) : 60;
    const tagY = gridLabelY + labelSize + tagGap;

    // Calculate total tags height
    const tagsHeight =
      tags.length > 0
        ? tags.length * tagHeightVal + (tags.length - 1) * tagRowGap
        : 0;

    // Logo gap adjusts based on number of tags
    const logoGap = tags.length > 0 ? Math.max(30, 60 - tags.length * 8) : 60;
    const logoY =
      tags.length > 0
        ? tagY + tagsHeight + logoGap
        : gridLabelY + labelSize + 60;
    const dynamicHeight = Math.max(OV_HEIGHT, logoY + logoHeight + padding.top);

    // Get duration from training log (duration is in minutes)
    const timeValue = trainingLog.duration
      ? formatMinutes(trainingLog.duration)
      : "-";

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${OV_WIDTH}" height="${dynamicHeight}" viewBox="0 0 ${OV_WIDTH} ${dynamicHeight}">
  <defs>
    <style type="text/css">
      ${geistMonoFontData ? `@font-face { font-family: 'Geist Mono'; src: url('${geistMonoFontData}') format('woff2'); font-weight: 700; font-style: normal; }` : ""}
      .ov9-value {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${valueSize}px;
        fill: #FFFFFF;
        font-weight: 700;
      }
      .ov9-label {
        font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${labelSize}px;
        fill: rgba(255, 255, 255, 0.7);
        font-weight: 400;
      }
      .ov9-tag {
        font-family: 'Geist Mono', ui-monospace, monospace;
        font-size: ${tagSize}px;
        fill: rgba(255, 255, 255, 0.85);
        font-weight: 700;
      }
    </style>
  </defs>

  <!-- PEAK Logo - centered -->
  <!-- Load (Left) -->
  <text class="ov9-value" x="${leftColX}" y="${gridValueY}" text-anchor="middle" dominant-baseline="central">${Math.round(trainingLog.load || 0)}</text>
  <text class="ov9-label" x="${leftColX}" y="${gridLabelY}" text-anchor="middle" dominant-baseline="hanging">Load</text>

  <!-- Duration (Right) -->
  <text class="ov9-value" x="${rightColX}" y="${gridValueY}" text-anchor="middle" dominant-baseline="central">${timeValue}</text>
  <text class="ov9-label" x="${rightColX}" y="${gridLabelY}" text-anchor="middle" dominant-baseline="hanging">Duration</text>

  ${
    tags.length > 0
      ? (() => {
          const maxTagWidth = OV_WIDTH - padding.left - padding.right;

          // Stack vertically: one tag per row, variable width centered
          let currentTagY = tagY;
          return `<!-- Session Tags -->
  ${tags
    .map((t: string) => {
      const textWidth = t.length * tagCharWidth;
      const boxWidth = Math.min(textWidth + tagPadX * 2, maxTagWidth);
      const x = centerX - boxWidth / 2;
      const y = currentTagY;
      currentTagY += tagHeightVal + tagRowGap;
      return `<g transform="translate(${x}, ${y})">
    <clipPath id="ov9-clip-${tags.indexOf(t)}"><rect x="0" y="0" width="${boxWidth}" height="${tagHeightVal}" rx="6" ry="6"/></clipPath>
    <rect x="0" y="0" width="${boxWidth}" height="${tagHeightVal}" rx="6" ry="6" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.3)" stroke-width="1.5"/>
    <text class="ov9-tag" x="${boxWidth / 2}" y="${tagHeightVal / 2}" text-anchor="middle" dominant-baseline="central" clip-path="url(#ov9-clip-${tags.indexOf(t)})">${t}</text>
  </g>`;
    })
    .join("\n  ")}`;
        })()
      : ""
  }

  <!-- PEAK Logo - bottom -->
  <g transform="translate(${centerX - logoWidth / 2}, ${logoY})">
    ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
  </g>
</svg>`;
  }, [trainingLog, logoDataUrl, geistMonoFontData]);

  // GPS Overlay 6: Distance + Load + Time + Map
  const buildOverlay6SVG = useCallback((): string => {
    const GPS_WIDTH = 1080;
    const padding = 80;
    const logoWidth = 240;
    const logoHeight = logoWidth * (44.8 / 112);
    const metricHeight = 140;
    const mapHeight = 400;
    const distanceTop = padding;
    const loadTop = distanceTop + metricHeight + 60;
    const timeTop = loadTop + metricHeight + 60;
    const mapTop = timeTop + metricHeight + 80;
    const mapWidth = GPS_WIDTH - padding * 2;
    const lastContentBottom =
      positionValues.length > 0 ? mapTop + mapHeight : timeTop + metricHeight;
    const logoBottomY = lastContentBottom + 60;
    const contentHeight = logoBottomY + logoHeight + padding;

    const routePath =
      positionValues.length > 0
        ? generateRoutePath(positionValues, mapWidth, mapHeight)
        : "";

    // Calculate start/end points for markers
    let startPoint: [number, number] | null = null;
    let endPoint: [number, number] | null = null;
    if (positionValues.length > 0) {
      const lats = positionValues.map((p) => p.coords_lat_lng_deg[0]);
      const lngs = positionValues.map((p) => p.coords_lat_lng_deg[1]);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      const latRange = maxLat - minLat || 0.001;
      const lngRange = maxLng - minLng || 0.001;
      const paddingPercent = 0.1;
      const paddedMinLat = minLat - latRange * paddingPercent;
      const paddedMaxLat = maxLat + latRange * paddingPercent;
      const paddedMinLng = minLng - lngRange * paddingPercent;
      const paddedMaxLng = maxLng + lngRange * paddingPercent;
      const avgLat = (minLat + maxLat) / 2;
      const latMetersPerDegree = 111000;
      const lngMetersPerDegree = 111000 * Math.cos((avgLat * Math.PI) / 180);
      const latRangeMeters = (paddedMaxLat - paddedMinLat) * latMetersPerDegree;
      const lngRangeMeters = (paddedMaxLng - paddedMinLng) * lngMetersPerDegree;
      const gpsAspectRatio = lngRangeMeters / latRangeMeters;
      const containerAspectRatio = mapWidth / mapHeight;
      let renderWidth = mapWidth;
      let renderHeight = mapHeight;
      let offsetX = 0;
      let offsetY = 0;
      if (gpsAspectRatio > containerAspectRatio) {
        renderHeight = mapWidth / gpsAspectRatio;
        offsetY = (mapHeight - renderHeight) / 2;
      } else {
        renderWidth = mapHeight * gpsAspectRatio;
        offsetX = (mapWidth - renderWidth) / 2;
      }
      const startPos = positionValues[0];
      const startX =
        offsetX +
        ((startPos.coords_lat_lng_deg[1] - paddedMinLng) /
          (paddedMaxLng - paddedMinLng)) *
          renderWidth;
      const startY =
        offsetY +
        ((paddedMaxLat - startPos.coords_lat_lng_deg[0]) /
          (paddedMaxLat - paddedMinLat)) *
          renderHeight;
      startPoint = [startX, startY];
      const endPos = positionValues[positionValues.length - 1];
      const endX =
        offsetX +
        ((endPos.coords_lat_lng_deg[1] - paddedMinLng) /
          (paddedMaxLng - paddedMinLng)) *
          renderWidth;
      const endY =
        offsetY +
        ((paddedMaxLat - endPos.coords_lat_lng_deg[0]) /
          (paddedMaxLat - paddedMinLat)) *
          renderHeight;
      endPoint = [endX, endY];
    }

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${GPS_WIDTH}" height="${contentHeight}" viewBox="0 0 ${GPS_WIDTH} ${contentHeight}">
  <defs>
    <style type="text/css">
      ${calSansFontData ? `@font-face { font-family: 'Cal Sans'; src: url('${calSansFontData}') format('woff2'); font-weight: 400; font-style: normal; }` : ""}
      .title { font-family: "Funnel Sans", sans-serif; font-size: 32px; fill: #FFFFFF; text-anchor: middle; font-weight: 500; opacity: 0.7; }
      .value { font-family: "Funnel Sans", sans-serif; font-size: 96px; fill: #FFFFFF; font-weight: 700; text-anchor: middle; }
    </style>
  </defs>
  <g transform="translate(${GPS_WIDTH / 2}, ${distanceTop + metricHeight / 2})">
    <text class="value" y="-20">${distance >= 1 ? `${distance.toFixed(2)} km` : `${(distance * 1000).toFixed(0)} m`}</text>
    <text class="title" y="50">Distance</text>
  </g>
  <g transform="translate(${GPS_WIDTH / 2}, ${loadTop + metricHeight / 2})">
    <text class="value" y="-20">${Math.round(trainingLog.load || 0)}</text>
    <text class="title" y="50">Load</text>
  </g>
  <g transform="translate(${GPS_WIDTH / 2}, ${timeTop + metricHeight / 2})">
    <text class="value" y="-20">${formatMinutes(trainingLog.duration || 0)}</text>
    <text class="title" y="50">Duration</text>
  </g>
  ${
    routePath
      ? `
  <g transform="translate(${padding}, ${mapTop})">
    <path d="${routePath}" stroke="#FFFFFF" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.8" />
    ${startPoint ? `<g transform="translate(${startPoint[0] - 12}, ${startPoint[1] - 20})"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#86efac" stroke="white" stroke-width="2"/></g>` : ""}
    ${endPoint ? `<g transform="translate(${endPoint[0] - 12}, ${endPoint[1] - 20})"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#fca5a5" stroke="white" stroke-width="2"/></g>` : ""}
  </g>`
      : ""
  }
  <!-- PEAK Logo - bottom -->
  <g transform="translate(${GPS_WIDTH / 2 - logoWidth / 2}, ${logoBottomY})">
    ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
  </g>
</svg>`;
  }, [
    positionValues,
    distance,
    trainingLog.load,
    trainingLog.duration,
    logoDataUrl,
    calSansFontData,
  ]);

  // GPS Overlay 7: Load + Avg HR + Max HR + Time + Map
  const buildOverlay7SVG = useCallback((): string => {
    const GPS_WIDTH = 1080;
    const padding = 80;
    const logoWidth = 240;
    const logoHeight = logoWidth * (44.8 / 112);
    const metricHeight = 140;
    const mapHeight = 400;
    const loadTop = padding;
    let currentTop = loadTop + metricHeight + 60;
    let avgHRTop = 0;
    let maxHRTop = 0;
    if (hasHRData) {
      avgHRTop = currentTop;
      currentTop = avgHRTop + metricHeight + 60;
      maxHRTop = currentTop;
      currentTop = maxHRTop + metricHeight + 60;
    }
    const timeTop = currentTop;
    const mapTop = timeTop + metricHeight + 80;
    const mapWidth = GPS_WIDTH - padding * 2;
    const lastContentBottom =
      positionValues.length > 0 ? mapTop + mapHeight : timeTop + metricHeight;
    const logoBottomY = lastContentBottom + 60;
    const contentHeight = logoBottomY + logoHeight + padding;
    const routePath =
      positionValues.length > 0
        ? generateRoutePath(positionValues, mapWidth, mapHeight)
        : "";

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${GPS_WIDTH}" height="${contentHeight}" viewBox="0 0 ${GPS_WIDTH} ${contentHeight}">
  <defs>
    <style type="text/css">
      ${calSansFontData ? `@font-face { font-family: 'Cal Sans'; src: url('${calSansFontData}') format('woff2'); }` : ""}
      .title { font-family: "Funnel Sans", sans-serif; font-size: 32px; fill: #FFFFFF; text-anchor: middle; font-weight: 500; opacity: 0.7; }
      .value { font-family: "Funnel Sans", sans-serif; font-size: 96px; fill: #FFFFFF; font-weight: 700; text-anchor: middle; }
    </style>
  </defs>
  <g transform="translate(${GPS_WIDTH / 2}, ${loadTop + metricHeight / 2})">
    <text class="value" y="-20">${Math.round(trainingLog.load || 0)}</text>
    <text class="title" y="50">Load</text>
  </g>
  ${
    hasHRData
      ? `
  <g transform="translate(${GPS_WIDTH / 2}, ${avgHRTop + metricHeight / 2})">
    <text class="value" y="-20">${Math.round(avgHR || 0)}</text>
    <text class="title" y="50">Average HR</text>
  </g>
  <g transform="translate(${GPS_WIDTH / 2}, ${maxHRTop + metricHeight / 2})">
    <text class="value" y="-20">${Math.round(maxHR || 0)}</text>
    <text class="title" y="50">Max HR</text>
  </g>`
      : ""
  }
  <g transform="translate(${GPS_WIDTH / 2}, ${timeTop + metricHeight / 2})">
    <text class="value" y="-20">${formatMinutes(trainingLog.duration || 0)}</text>
    <text class="title" y="50">Duration</text>
  </g>
  ${routePath ? `<g transform="translate(${padding}, ${mapTop})"><path d="${routePath}" stroke="#FFFFFF" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.8" /></g>` : ""}
  <!-- PEAK Logo - bottom -->
  <g transform="translate(${GPS_WIDTH / 2 - logoWidth / 2}, ${logoBottomY})">
    ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
  </g>
</svg>`;
  }, [
    positionValues,
    trainingLog.load,
    trainingLog.duration,
    logoDataUrl,
    calSansFontData,
    hasHRData,
    avgHR,
    maxHR,
  ]);

  // GPS Overlay 8: 2x2 Grid + Map
  const buildOverlay8SVG = useCallback((): string => {
    const GPS_WIDTH = 1080;
    const padding = 80;
    const sidePadding = 180;
    const logoWidth = 240;
    const logoHeight = logoWidth * (44.8 / 112);
    const gridTop = padding;
    const gridItemHeight = 180;
    const gridGap = 60;
    const mapHeight = 500;
    const mapTop = gridTop + gridItemHeight * 2 + gridGap + 80;
    const mapWidth = GPS_WIDTH - padding * 2;
    const lastContentBottom =
      positionValues.length > 0
        ? mapTop + mapHeight
        : gridTop + gridItemHeight * 2 + gridGap;
    const logoBottomY = lastContentBottom + 60;
    const contentHeight = logoBottomY + logoHeight + padding;
    const routePath =
      positionValues.length > 0
        ? generateRoutePath(positionValues, mapWidth, mapHeight)
        : "";
    const gridItemWidth = (GPS_WIDTH - sidePadding * 2 - gridGap) / 2;
    const col1X = sidePadding;
    const col2X = sidePadding + gridItemWidth + gridGap;
    const row1Y = gridTop;
    const row2Y = gridTop + gridItemHeight + gridGap;

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${GPS_WIDTH}" height="${contentHeight}" viewBox="0 0 ${GPS_WIDTH} ${contentHeight}">
  <defs>
    <style type="text/css">
      ${calSansFontData ? `@font-face { font-family: 'Cal Sans'; src: url('${calSansFontData}') format('woff2'); }` : ""}
      .grid-title { font-family: "Funnel Sans", sans-serif; font-size: 32px; fill: #FFFFFF; text-anchor: middle; font-weight: 500; opacity: 0.7; }
      .grid-value { font-family: "Funnel Sans", sans-serif; font-size: 96px; fill: #FFFFFF; font-weight: 700; text-anchor: middle; }
    </style>
  </defs>
  <g transform="translate(${col1X + gridItemWidth / 2}, ${row1Y + gridItemHeight / 2})">
    <text class="grid-value" y="-15">${Math.round(trainingLog.load || 0)}</text>
    <text class="grid-title" y="63">Load</text>
  </g>
  <g transform="translate(${col2X + gridItemWidth / 2}, ${row1Y + gridItemHeight / 2})">
    <text class="grid-value" y="-15">${formatMinutes(trainingLog.duration || 0)}</text>
    <text class="grid-title" y="63">Duration</text>
  </g>
  <g transform="translate(${col1X + gridItemWidth / 2}, ${row2Y + gridItemHeight / 2})">
    <text class="grid-value" y="-15">${hasHRData ? Math.round(avgHR || 0) : "-"}</text>
    <text class="grid-title" y="63">Average HR</text>
  </g>
  <g transform="translate(${col2X + gridItemWidth / 2}, ${row2Y + gridItemHeight / 2})">
    <text class="grid-value" y="-15">${hasHRData ? Math.round(maxHR || 0) : "-"}</text>
    <text class="grid-title" y="63">Max HR</text>
  </g>
  ${routePath ? `<g transform="translate(${padding}, ${mapTop})"><path d="${routePath}" stroke="#FFFFFF" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.8" /></g>` : ""}
  <!-- PEAK Logo - bottom -->
  <g transform="translate(${GPS_WIDTH / 2 - logoWidth / 2}, ${logoBottomY})">
    ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
  </g>
</svg>`;
  }, [
    positionValues,
    trainingLog.load,
    trainingLog.duration,
    logoDataUrl,
    calSansFontData,
    hasHRData,
    avgHR,
    maxHR,
  ]);

  // GPS Overlay 10: Map & Logo only
  const buildOverlay10SVG = useCallback((): string => {
    const GPS_WIDTH = 1080;
    const padding = 80;
    const logoWidth = 240;
    const logoHeight = logoWidth * (44.8 / 112);
    const mapTop = padding;
    const mapHeight = 600;
    const mapWidth = GPS_WIDTH - padding * 2;
    const logoBottomY = mapTop + mapHeight + 60;
    const contentHeight = logoBottomY + logoHeight + padding;

    const routePath =
      positionValues.length > 0
        ? generateRoutePath(positionValues, mapWidth, mapHeight)
        : "";

    // Calculate start/end points for markers
    let startPoint: [number, number] | null = null;
    let endPoint: [number, number] | null = null;
    if (positionValues.length > 0) {
      const lats = positionValues.map((p) => p.coords_lat_lng_deg[0]);
      const lngs = positionValues.map((p) => p.coords_lat_lng_deg[1]);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      const latRange = maxLat - minLat || 0.001;
      const lngRange = maxLng - minLng || 0.001;
      const paddingPercent = 0.1;
      const paddedMinLat = minLat - latRange * paddingPercent;
      const paddedMaxLat = maxLat + latRange * paddingPercent;
      const paddedMinLng = minLng - lngRange * paddingPercent;
      const paddedMaxLng = maxLng + lngRange * paddingPercent;
      const avgLat = (minLat + maxLat) / 2;
      const latMetersPerDegree = 111000;
      const lngMetersPerDegree = 111000 * Math.cos((avgLat * Math.PI) / 180);
      const latRangeMeters = (paddedMaxLat - paddedMinLat) * latMetersPerDegree;
      const lngRangeMeters = (paddedMaxLng - paddedMinLng) * lngMetersPerDegree;
      const gpsAspectRatio = lngRangeMeters / latRangeMeters;
      const containerAspectRatio = mapWidth / mapHeight;
      let renderWidth = mapWidth;
      let renderHeight = mapHeight;
      let offsetX = 0;
      let offsetY = 0;
      if (gpsAspectRatio > containerAspectRatio) {
        renderHeight = mapWidth / gpsAspectRatio;
        offsetY = (mapHeight - renderHeight) / 2;
      } else {
        renderWidth = mapHeight * gpsAspectRatio;
        offsetX = (mapWidth - renderWidth) / 2;
      }
      const startPos = positionValues[0];
      const startX =
        offsetX +
        ((startPos.coords_lat_lng_deg[1] - paddedMinLng) /
          (paddedMaxLng - paddedMinLng)) *
          renderWidth;
      const startY =
        offsetY +
        ((paddedMaxLat - startPos.coords_lat_lng_deg[0]) /
          (paddedMaxLat - paddedMinLat)) *
          renderHeight;
      startPoint = [startX, startY];
      const endPos = positionValues[positionValues.length - 1];
      const endX =
        offsetX +
        ((endPos.coords_lat_lng_deg[1] - paddedMinLng) /
          (paddedMaxLng - paddedMinLng)) *
          renderWidth;
      const endY =
        offsetY +
        ((paddedMaxLat - endPos.coords_lat_lng_deg[0]) /
          (paddedMaxLat - paddedMinLat)) *
          renderHeight;
      endPoint = [endX, endY];
    }

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${GPS_WIDTH}" height="${contentHeight}" viewBox="0 0 ${GPS_WIDTH} ${contentHeight}">
  <defs>
    <style type="text/css">
      ${calSansFontData ? `@font-face { font-family: 'Cal Sans'; src: url('${calSansFontData}') format('woff2'); }` : ""}
    </style>
  </defs>
  ${
    routePath
      ? `
  <g transform="translate(${padding}, ${mapTop})">
    <path d="${routePath}" stroke="#FFFFFF" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.8" />
    ${startPoint ? `<g transform="translate(${startPoint[0] - 12}, ${startPoint[1] - 20})"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#86efac" stroke="white" stroke-width="2"/></g>` : ""}
    ${endPoint ? `<g transform="translate(${endPoint[0] - 12}, ${endPoint[1] - 20})"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#fca5a5" stroke="white" stroke-width="2"/></g>` : ""}
  </g>`
      : ""
  }
  <!-- PEAK Logo - bottom -->
  <g transform="translate(${GPS_WIDTH / 2 - logoWidth / 2}, ${logoBottomY})">
    ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
  </g>
</svg>`;
  }, [positionValues, logoDataUrl, calSansFontData]);

  // GPS Overlay 11: Map + Training Tags + Load & Logo
  const buildOverlay11SVG = useCallback((): string => {
    const GPS_WIDTH = 1080;
    const padding = 80;
    const logoWidth = 240;
    const logoHeight = logoWidth * (44.8 / 112);
    const loadTop = padding;
    const metricHeight = 140;
    const tagSize = 22;
    const tagPadY = 16;
    const tagPadX = 40;
    const tagRowGap = 12;
    const tagCharWidth = tagSize * 0.62;
    const tags =
      trainingLog.tag && trainingLog.tag.length > 0 ? trainingLog.tag : [];

    // Calculate tag section height
    const tagHeightVal = tagSize + tagPadY * 2;
    const tagsHeight =
      tags.length > 0
        ? tags.length * tagHeightVal + (tags.length - 1) * tagRowGap
        : 0;

    // Dynamic gaps that shrink with more tags
    const tagToMapGap =
      tags.length > 0 ? Math.max(24, 50 - tags.length * 8) : 50;
    const mapToLogoGap = Math.max(24, 50 - tags.length * 6);

    const tagTop = loadTop + metricHeight + 40;
    const mapTop =
      tags.length > 0
        ? tagTop + tagsHeight + tagToMapGap
        : loadTop + metricHeight + 40;
    const mapHeight = 500;
    const mapWidth = GPS_WIDTH - padding * 2;
    const logoBottomY = mapTop + mapHeight + mapToLogoGap;
    const contentHeight = logoBottomY + logoHeight + padding;

    const routePath =
      positionValues.length > 0
        ? generateRoutePath(positionValues, mapWidth, mapHeight)
        : "";

    // Calculate start/end points for markers
    let startPoint: [number, number] | null = null;
    let endPoint: [number, number] | null = null;
    if (positionValues.length > 0) {
      const lats = positionValues.map((p) => p.coords_lat_lng_deg[0]);
      const lngs = positionValues.map((p) => p.coords_lat_lng_deg[1]);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      const latRange = maxLat - minLat || 0.001;
      const lngRange = maxLng - minLng || 0.001;
      const paddingPercent = 0.1;
      const paddedMinLat = minLat - latRange * paddingPercent;
      const paddedMaxLat = maxLat + latRange * paddingPercent;
      const paddedMinLng = minLng - lngRange * paddingPercent;
      const paddedMaxLng = maxLng + lngRange * paddingPercent;
      const avgLat = (minLat + maxLat) / 2;
      const latMetersPerDegree = 111000;
      const lngMetersPerDegree = 111000 * Math.cos((avgLat * Math.PI) / 180);
      const latRangeMeters = (paddedMaxLat - paddedMinLat) * latMetersPerDegree;
      const lngRangeMeters = (paddedMaxLng - paddedMinLng) * lngMetersPerDegree;
      const gpsAspectRatio = lngRangeMeters / latRangeMeters;
      const containerAspectRatio = mapWidth / mapHeight;
      let renderWidth = mapWidth;
      let renderHeight = mapHeight;
      let offsetX = 0;
      let offsetY = 0;
      if (gpsAspectRatio > containerAspectRatio) {
        renderHeight = mapWidth / gpsAspectRatio;
        offsetY = (mapHeight - renderHeight) / 2;
      } else {
        renderWidth = mapHeight * gpsAspectRatio;
        offsetX = (mapWidth - renderWidth) / 2;
      }
      const startPos = positionValues[0];
      const startX =
        offsetX +
        ((startPos.coords_lat_lng_deg[1] - paddedMinLng) /
          (paddedMaxLng - paddedMinLng)) *
          renderWidth;
      const startY =
        offsetY +
        ((paddedMaxLat - startPos.coords_lat_lng_deg[0]) /
          (paddedMaxLat - paddedMinLat)) *
          renderHeight;
      startPoint = [startX, startY];
      const endPos = positionValues[positionValues.length - 1];
      const endX =
        offsetX +
        ((endPos.coords_lat_lng_deg[1] - paddedMinLng) /
          (paddedMaxLng - paddedMinLng)) *
          renderWidth;
      const endY =
        offsetY +
        ((paddedMaxLat - endPos.coords_lat_lng_deg[0]) /
          (paddedMaxLat - paddedMinLat)) *
          renderHeight;
      endPoint = [endX, endY];
    }

    const maxTagWidth = GPS_WIDTH - padding * 2;
    const centerX = GPS_WIDTH / 2;

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${GPS_WIDTH}" height="${contentHeight}" viewBox="0 0 ${GPS_WIDTH} ${contentHeight}">
  <defs>
    <style type="text/css">
      ${geistMonoFontData ? `@font-face { font-family: 'Geist Mono'; src: url('${geistMonoFontData}') format('woff2'); font-weight: 700; font-style: normal; }` : ""}
      ${calSansFontData ? `@font-face { font-family: 'Cal Sans'; src: url('${calSansFontData}') format('woff2'); }` : ""}
      .ov11-title { font-family: "Funnel Sans", sans-serif; font-size: 32px; fill: #FFFFFF; text-anchor: middle; font-weight: 500; opacity: 0.7; }
      .ov11-value { font-family: "Funnel Sans", sans-serif; font-size: 96px; fill: #FFFFFF; font-weight: 700; text-anchor: middle; }
      .ov11-tag { font-family: "Geist Mono", ui-monospace, monospace; font-size: ${tagSize}px; fill: rgba(255,255,255,0.85); font-weight: 700; }
    </style>
  </defs>
  <g transform="translate(${centerX}, ${loadTop + metricHeight / 2})">
    <text class="ov11-value" y="-20">${Math.round(trainingLog.load || 0)}</text>
    <text class="ov11-title" y="50">Load</text>
  </g>
  ${
    tags.length > 0
      ? (() => {
          let currentTagY = tagTop;
          return tags
            .map((t: string) => {
              const textWidth = t.length * tagCharWidth;
              const boxWidth = Math.min(textWidth + tagPadX * 2, maxTagWidth);
              const x = centerX - boxWidth / 2;
              const y = currentTagY;
              currentTagY += tagHeightVal + tagRowGap;
              return `<g transform="translate(${x}, ${y})">
    <clipPath id="ov11-clip-${tags.indexOf(t)}"><rect x="0" y="0" width="${boxWidth}" height="${tagHeightVal}" rx="6" ry="6"/></clipPath>
    <rect x="0" y="0" width="${boxWidth}" height="${tagHeightVal}" rx="6" ry="6" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.3)" stroke-width="1.5"/>
    <text class="ov11-tag" x="${boxWidth / 2}" y="${tagHeightVal / 2}" text-anchor="middle" dominant-baseline="central" clip-path="url(#ov11-clip-${tags.indexOf(t)})">${t}</text>
  </g>`;
            })
            .join("\n  ");
        })()
      : ""
  }
  ${
    routePath
      ? `
  <g transform="translate(${padding}, ${mapTop})">
    <path d="${routePath}" stroke="#FFFFFF" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.8" />
    ${startPoint ? `<g transform="translate(${startPoint[0] - 12}, ${startPoint[1] - 20})"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#86efac" stroke="white" stroke-width="2"/></g>` : ""}
    ${endPoint ? `<g transform="translate(${endPoint[0] - 12}, ${endPoint[1] - 20})"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#fca5a5" stroke="white" stroke-width="2"/></g>` : ""}
  </g>`
      : ""
  }
  <!-- PEAK Logo - bottom -->
  <g transform="translate(${centerX - logoWidth / 2}, ${logoBottomY})">
    ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoWidth}" height="${logoHeight}" />` : ""}
  </g>
</svg>`;
  }, [
    positionValues,
    trainingLog,
    logoDataUrl,
    calSansFontData,
    geistMonoFontData,
  ]);

  // Main buildSVG function - switches between overlays
  const buildSVG = useCallback((): string => {
    switch (selectedOverlay) {
      case 11:
        return buildOverlay11SVG();
      case 10:
        return buildOverlay10SVG();
      case 9:
        return buildOverlay9SVG();
      case 8:
        return buildOverlay8SVG();
      case 7:
        return buildOverlay7SVG();
      case 6:
        return buildOverlay6SVG();
      case 5:
        return buildOverlay5SVG();
      case 4:
        return buildOverlay4SVG();
      case 3:
        return buildOverlay3SVG();
      case 2:
        return buildOverlay2SVG();
      case 1:
      default:
        return buildOverlay1SVG();
    }
  }, [
    selectedOverlay,
    buildOverlay1SVG,
    buildOverlay2SVG,
    buildOverlay3SVG,
    buildOverlay4SVG,
    buildOverlay5SVG,
    buildOverlay6SVG,
    buildOverlay7SVG,
    buildOverlay8SVG,
    buildOverlay9SVG,
    buildOverlay10SVG,
    buildOverlay11SVG,
  ]);

  const handleDownload = useCallback(async () => {
    try {
      setIsExporting(true);
      const svgContent = buildSVG();
      const blob = new Blob([svgContent], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);

      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = (e) => reject(e);
        img.src = url;
      });

      // Wait for fonts to be fully loaded (longer timeout for iOS Safari)
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      await new Promise((resolve) => setTimeout(resolve, isIOS ? 500 : 100));

      // Use the actual image dimensions (which come from the SVG)
      const canvasWidth = img.naturalWidth || EXPORT_WIDTH;
      const canvasHeight = img.naturalHeight || EXPORT_HEIGHT;

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not supported");

      // Clear canvas with transparent background
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Draw the image
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (!blob) throw new Error("Failed to create PNG");

          const dlUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = dlUrl;
          a.download = `training-overlay-${selectedOverlay}-${Date.now()}.png`;
          document.body.appendChild(a);
          a.click();

          // Cleanup
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(dlUrl);
            URL.revokeObjectURL(url);
          }, 100);
        },
        "image/png",
        1.0,
      );
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  }, [buildSVG, selectedOverlay]);
  // Simple preview (HTML) – the export uses the SVG above for fidelity
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden sm:overflow-y-auto">
          <div className="flex h-full sm:min-h-full sm:items-center sm:justify-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full h-full sm:h-auto sm:max-w-md transform overflow-hidden sm:rounded-lg text-left align-middle shadow-xl transition-all flex flex-col relative"
                style={{
                  backgroundImage: `url(${transparentPattern})`,
                  backgroundRepeat: "repeat",
                  backgroundSize: "cover",
                }}
              >
                {/* Black blur overlay for the entire dialog */}
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

                <div className="shrink-0 z-10 bg-base-100 sm:bg-transparent">
                  <div className="flex justify-between items-center p-3 px-6 sm:p-4 sm:px-8">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-base-content sm:text-white font-grotesk">
                        Share Data Overlay
                      </h3>
                      <button
                        onClick={() => setShowInfoDialog(true)}
                        className="p-1 hover:bg-base-200 sm:hover:bg-base-100/10 rounded-full transition-colors"
                      >
                        <HiQuestionMarkCircle className="w-5 h-5 text-base-content/50 hover:text-base-content/80 sm:text-white/60 sm:hover:text-white" />
                      </button>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-base-200 sm:hover:bg-base-100/10 rounded-full transition-colors"
                    >
                      <HiX className="w-5 h-5 text-base-content/60 sm:text-white/70" />
                    </button>
                  </div>

                  {/* Chevron Overlay Navigator */}
                </div>

                {/* Info Dialog */}
                <Transition appear show={showInfoDialog} as={Fragment}>
                  <Dialog
                    as="div"
                    className="relative z-[60]"
                    onClose={() => setShowInfoDialog(false)}
                  >
                    <Transition.Child
                      as={Fragment}
                      enter="ease-out duration-200"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="ease-in duration-150"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                      <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                          as={Fragment}
                          enter="ease-out duration-200"
                          enterFrom="opacity-0 scale-95"
                          enterTo="opacity-100 scale-100"
                          leave="ease-in duration-150"
                          leaveFrom="opacity-100 scale-100"
                          leaveTo="opacity-0 scale-95"
                        >
                          <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-xl bg-base-100 p-8 shadow-xl transition-all">
                            <Dialog.Title className="text-lg font-semibold text-base-content font-grotesk mb-4">
                              How to Use
                            </Dialog.Title>
                            <div className="space-y-2 text-sm text-base-content/80">
                              <p>
                                1. Browse through the overlay styles using the
                                arrows or dots
                              </p>
                              <p>
                                2. Download your preferred overlay as a
                                transparent PNG
                              </p>
                              <p>
                                3. Open your training photo in any editing app
                              </p>
                              <p>
                                4. Import the downloaded overlay and position it
                                over your photo
                              </p>
                              <p>
                                5. Share your training session with your data
                                displayed
                              </p>
                            </div>
                            <button
                              onClick={() => setShowInfoDialog(false)}
                              className="mt-6 w-full py-2.5 bg-neutral text-neutral-content rounded-lg font-semibold hover:bg-neutral-focus transition-colors"
                            >
                              Got it
                            </button>
                          </Dialog.Panel>
                        </Transition.Child>
                      </div>
                    </div>
                  </Dialog>
                </Transition>

                <div className="flex-1 min-h-0 flex flex-col justify-center px-4 sm:px-8 py-2 sm:py-4 relative z-10">
                  {(() => {
                    // Build the ordered list of available overlays based on data availability
                    const availableOverlays: {
                      id: OverlayId;
                      label: string;
                    }[] = (() => {
                      const overlays: { id: OverlayId; label: string }[] = [];

                      // GPS overlays (6, 7, 8, 10, 11) - shown when GPS data available
                      if (hasGPSData) {
                        overlays.push({ id: 10, label: "Route" });
                        overlays.push({ id: 11, label: "Route + Tags" });
                        overlays.push({ id: 6, label: "Route + Stats" });
                        overlays.push({ id: 7, label: "Route + HR" });
                        overlays.push({ id: 8, label: "Route Grid" });
                      }

                      // HR-requiring overlays (1, 2, 3) - shown when HR data available
                      // Overlays 1, 2 use peak score so hide when score is 0 or free user
                      if (heartRateValues.length > 0) {
                        if (showPeakScoreOverlays) {
                          overlays.push({ id: 1, label: "Peak Readiness" });
                          overlays.push({ id: 2, label: "Full Stats" });
                        }
                        overlays.push({ id: 3, label: "Grid" });
                      }

                      // Overlays 4, 5 use peak score so hide when score is 0 or free user
                      if (showPeakScoreOverlays) {
                        overlays.push({ id: 4, label: "Readiness" });
                        overlays.push({ id: 5, label: "Score + Metrics" });
                      }
                      overlays.push({ id: 9, label: "Load + Time" });

                      return overlays;
                    })();

                    const currentIndex = availableOverlays.findIndex(
                      (o) => o.id === selectedOverlay,
                    );
                    // If selected overlay is no longer available, switch to first available
                    if (
                      currentIndex === -1 &&
                      availableOverlays.length > 0 &&
                      selectedOverlay !== availableOverlays[0].id
                    ) {
                      setSelectedOverlay(availableOverlays[0].id);
                    }
                    const safeIndex = currentIndex === -1 ? 0 : currentIndex;
                    const canGoPrev = safeIndex > 0;
                    const canGoNext = safeIndex < availableOverlays.length - 1;

                    const goPrev = () => {
                      if (canGoPrev)
                        setSelectedOverlay(availableOverlays[safeIndex - 1].id);
                    };
                    const goNext = () => {
                      if (canGoNext)
                        setSelectedOverlay(availableOverlays[safeIndex + 1].id);
                    };

                    return (
                      <div className="wrapper relative rounded-md aspect-[4/5] sm:aspect-[1/1.5] overflow-hidden max-h-[calc(100vh-10rem)] sm:max-h-none">
                        <div className="absolute top-6 left-4 px-2 py-0.5 rounded-md bg-base-200 bg-opacity-10 z-20">
                          <p className="text-[10px] uppercase text-base-content/50">
                            Transparent
                          </p>
                        </div>

                        {/* Left Chevron — overlaid on preview */}
                        <button
                          onClick={goPrev}
                          disabled={!canGoPrev}
                          className={`absolute left-2 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full transition-all ${
                            canGoPrev
                              ? "bg-black/40 text-white hover:bg-neutral/60"
                              : "opacity-0 pointer-events-none"
                          }`}
                        >
                          <HiChevronLeft className="w-8 h-8" />
                        </button>

                        {/* Right Chevron — overlaid on preview */}
                        <button
                          onClick={goNext}
                          disabled={!canGoNext}
                          className={`absolute right-2 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full transition-all ${
                            canGoNext
                              ? "bg-black/40 text-white hover:bg-neutral/60"
                              : "opacity-0 pointer-events-none"
                          }`}
                        >
                          <HiChevronRight className="w-8 h-8" />
                        </button>

                        {/* Dot indicators — bottom center of preview */}
                        <div className="absolute bottom-3 left-0 right-0 z-20 flex justify-center items-center gap-1.5">
                          {availableOverlays.map((o, i) => (
                            <button
                              key={o.id}
                              onClick={() => setSelectedOverlay(o.id)}
                              className={`rounded-full transition-all ${
                                i === safeIndex
                                  ? "w-4 h-1.5 bg-base-100"
                                  : "w-1.5 h-1.5 bg-base-100/40 hover:bg-base-100/70"
                              }`}
                            />
                          ))}
                        </div>

                        {/* Preview overlay content */}
                        <div className="absolute inset-0 p-3 pt-8 pb-8 sm:p-4 sm:pt-10 sm:pb-10 flex items-center justify-center bg-transparent overflow-hidden">
                          {selectedOverlay === 10 ? (
                            /* Overlay 10: GPS Map & Logo Preview */
                            <div className="flex flex-col items-center space-y-4 scale-[0.85] w-full px-4">
                              {positionValues.length > 0 && (
                                <svg
                                  viewBox="0 0 200 120"
                                  className="w-full h-28"
                                  preserveAspectRatio="xMidYMid meet"
                                >
                                  <path
                                    d={generateRoutePath(
                                      positionValues,
                                      200,
                                      120,
                                    )}
                                    stroke="#FFFFFF"
                                    strokeWidth={2}
                                    fill="none"
                                    opacity={0.8}
                                  />
                                </svg>
                              )}
                              <img
                                src={overlayLogoUrl}
                                alt="Peak"
                                className="w-28 h-auto pt-2"
                              />
                            </div>
                          ) : selectedOverlay === 11 ? (
                            /* Overlay 11: GPS Map + Tags + Load Preview */
                            <div className="flex flex-col items-center space-y-3 scale-[0.7] w-full px-4">
                              <div className="text-center">
                                <div className="text-4xl font-bold font-grotesk text-white">
                                  {Math.round(trainingLog.load || 0)}
                                </div>
                                <div className="text-sm text-white/70">
                                  Load
                                </div>
                              </div>
                              {trainingLog.tag &&
                                trainingLog.tag.length > 0 && (
                                  <div
                                    className={`flex flex-col items-stretch w-full px-4 ${trainingLog.tag.length > 3 ? "gap-1" : "gap-1.5"}`}
                                  >
                                    {trainingLog.tag.map((tag, index) => (
                                      <span
                                        key={index}
                                        className="flex items-center justify-center px-4 py-1.5 rounded-md text-sm font-bold font-geist text-white/85 border border-white/30 bg-white/20 truncate w-full min-w-0"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              {positionValues.length > 0 && (
                                <svg
                                  viewBox="0 0 200 80"
                                  className="w-full h-16"
                                  preserveAspectRatio="xMidYMid meet"
                                >
                                  <path
                                    d={generateRoutePath(
                                      positionValues,
                                      200,
                                      80,
                                    )}
                                    stroke="#FFFFFF"
                                    strokeWidth={2}
                                    fill="none"
                                    opacity={0.8}
                                  />
                                </svg>
                              )}
                              <img
                                src={overlayLogoUrl}
                                alt="Peak"
                                className="w-28 h-auto pt-2"
                              />
                            </div>
                          ) : selectedOverlay === 8 ? (
                            /* Overlay 8: GPS Grid Layout Preview */
                            <div className="flex flex-col items-center space-y-3 scale-[0.7] w-full px-4">
                              <div className="grid grid-cols-2 gap-x-6 gap-y-6 w-full px-4">
                                <div className="text-center">
                                  <div className="text-4xl font-bold font-grotesk text-white">
                                    {Math.round(trainingLog.load || 0)}
                                  </div>
                                  <div className="text-sm text-white/70">
                                    Load
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-4xl font-bold font-grotesk text-white">
                                    {formatMinutes(trainingLog.duration || 0)}
                                  </div>
                                  <div className="text-sm text-white/70">
                                    Duration
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-4xl font-bold font-grotesk text-white">
                                    {hasHRData ? Math.round(avgHR || 0) : "-"}
                                  </div>
                                  <div className="text-sm text-white/70">
                                    Average HR
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-4xl font-bold font-grotesk text-white">
                                    {hasHRData ? Math.round(maxHR || 0) : "-"}
                                  </div>
                                  <div className="text-sm text-white/70">
                                    Max HR
                                  </div>
                                </div>
                              </div>
                              {positionValues.length > 0 && (
                                <svg
                                  viewBox="0 0 200 100"
                                  className="w-full h-20"
                                  preserveAspectRatio="xMidYMid meet"
                                >
                                  <path
                                    d={generateRoutePath(
                                      positionValues,
                                      200,
                                      100,
                                    )}
                                    stroke="#FFFFFF"
                                    strokeWidth={2}
                                    fill="none"
                                    opacity={0.8}
                                  />
                                </svg>
                              )}
                              <img
                                src={overlayLogoUrl}
                                alt="Peak"
                                className="w-28 h-auto pt-2"
                              />
                            </div>
                          ) : selectedOverlay === 7 ? (
                            /* Overlay 7: GPS HR Stats Preview */
                            <div className="flex flex-col items-center space-y-3 scale-[0.7] w-full px-4">
                              <div className="text-center">
                                <div className="text-4xl font-bold font-grotesk text-white">
                                  {Math.round(trainingLog.load || 0)}
                                </div>
                                <div className="text-sm text-white/70">
                                  Load
                                </div>
                              </div>
                              {hasHRData && (
                                <>
                                  <div className="text-center">
                                    <div className="text-4xl font-bold font-grotesk text-white">
                                      {Math.round(avgHR || 0)}
                                    </div>
                                    <div className="text-sm text-white/70">
                                      Average HR
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-4xl font-bold font-grotesk text-white">
                                      {Math.round(maxHR || 0)}
                                    </div>
                                    <div className="text-sm text-white/70">
                                      Max HR
                                    </div>
                                  </div>
                                </>
                              )}
                              <div className="text-center">
                                <div className="text-4xl font-bold font-grotesk text-white">
                                  {formatMinutes(trainingLog.duration || 0)}
                                </div>
                                <div className="text-sm text-white/70">
                                  Duration
                                </div>
                              </div>
                              {positionValues.length > 0 && (
                                <svg
                                  viewBox="0 0 200 80"
                                  className="w-full h-16"
                                  preserveAspectRatio="xMidYMid meet"
                                >
                                  <path
                                    d={generateRoutePath(
                                      positionValues,
                                      200,
                                      80,
                                    )}
                                    stroke="#FFFFFF"
                                    strokeWidth={2}
                                    fill="none"
                                    opacity={0.8}
                                  />
                                </svg>
                              )}
                              <img
                                src={overlayLogoUrl}
                                alt="Peak"
                                className="w-28 h-auto pt-2"
                              />
                            </div>
                          ) : selectedOverlay === 6 ? (
                            /* Overlay 6: GPS Distance Stats Preview */
                            <div className="flex flex-col items-center space-y-3 scale-[0.7] w-full px-4">
                              <div className="text-center">
                                <div className="text-4xl font-bold font-grotesk text-white">
                                  {distance >= 1
                                    ? `${distance.toFixed(2)} km`
                                    : `${(distance * 1000).toFixed(0)} m`}
                                </div>
                                <div className="text-sm text-white/70">
                                  Distance
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-4xl font-bold font-grotesk text-white">
                                  {Math.round(trainingLog.load || 0)}
                                </div>
                                <div className="text-sm text-white/70">
                                  Load
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-4xl font-bold font-grotesk text-white">
                                  {formatMinutes(trainingLog.duration || 0)}
                                </div>
                                <div className="text-sm text-white/70">
                                  Duration
                                </div>
                              </div>
                              {positionValues.length > 0 && (
                                <svg
                                  viewBox="0 0 200 80"
                                  className="w-full h-16"
                                  preserveAspectRatio="xMidYMid meet"
                                >
                                  <path
                                    d={generateRoutePath(
                                      positionValues,
                                      200,
                                      80,
                                    )}
                                    stroke="#FFFFFF"
                                    strokeWidth={2}
                                    fill="none"
                                    opacity={0.8}
                                  />
                                </svg>
                              )}
                              <img
                                src={overlayLogoUrl}
                                alt="Peak"
                                className="w-28 h-auto pt-2"
                              />
                            </div>
                          ) : selectedOverlay === 5 ? (
                            /* Overlay 5: Radial Progress + Peak Score + Load & Time */
                            <div className="flex flex-col items-center justify-center space-y-4 scale-[0.95]">
                              {/* Radial Progress */}
                              <div className="text-center">
                                <div className="inline-block">
                                  <style>
                                    {`
                                  @import url('https://fonts.googleapis.com/css2?family=Funnel+Sans:wght@300;400;500;600;700&display=swap');

                                  .overlay5-radial-progress {
                                    background: transparent!important;
                                    box-shadow: inset 0 0 0 12px rgba(255, 255, 255, 0.15) !important;
                                    border-color: #ffffff !important;
                                  }
                                  .overlay5-radial-progress::before {
                                    border-color: #ffffff !important;
                                  }
                                  .overlay5-radial-progress span,
                                  .overlay5-radial-progress span * {
                                    color: white !important;
                                    font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
                                    font-weight: 700 !important;
                                  }
                                `}
                                  </style>
                                  <div
                                    style={{
                                      fontFamily:
                                        "Funnel Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
                                    }}
                                  >
                                    <RadialProgress
                                      value={Math.min(
                                        (peakScore / 110) * 100,
                                        100,
                                      )}
                                      size="9rem"
                                      thickness="12px"
                                      color="text-white overlay5-radial-progress"
                                      text={Math.round(peakScore).toString()}
                                      textSize="text-5xl"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Peak Score Label */}
                              <div className="text-center -mt-2">
                                <div className="text-lg font-normal font-grotesk text-white/70">
                                  Peak Readiness
                                </div>
                              </div>

                              {/* Bottom Grid: Load and Time */}
                              <div className="grid grid-cols-2 gap-8 w-full px-12 pt-4">
                                {/* Load */}
                                <div className="text-center">
                                  <div className="text-5xl font-bold font-grotesk text-white">
                                    {Math.round(trainingLog.load || 0)}
                                  </div>
                                  <div className="text-base text-white/70 font-grotesk mt-1">
                                    Load
                                  </div>
                                </div>
                                {/* Duration */}
                                <div className="text-center">
                                  <div className="text-5xl font-bold font-grotesk text-white">
                                    {trainingLog.duration
                                      ? formatMinutes(trainingLog.duration)
                                      : "-"}
                                  </div>
                                  <div className="text-base text-white/70 font-grotesk mt-1">
                                    Duration
                                  </div>
                                </div>
                              </div>
                              <img
                                src={overlayLogoUrl}
                                alt="Peak"
                                className="w-28 h-auto pt-2"
                              />
                            </div>
                          ) : selectedOverlay === 9 ? (
                            /* Overlay 9: Load + Time Only */
                            <div className="flex flex-col items-center justify-center space-y-4 scale-[0.8]">
                              {/* Load and Time Grid */}
                              <div className="grid grid-cols-2 gap-8 w-full px-12">
                                {/* Load */}
                                <div className="text-center">
                                  <div className="text-5xl font-bold font-grotesk text-white">
                                    {Math.round(trainingLog.load || 0)}
                                  </div>
                                  <div className="text-base text-white/70 font-grotesk mt-1">
                                    Load
                                  </div>
                                </div>
                                {/* Duration */}
                                <div className="text-center">
                                  <div className="text-5xl font-bold font-grotesk text-white">
                                    {trainingLog.duration
                                      ? formatMinutes(trainingLog.duration)
                                      : "-"}
                                  </div>
                                  <div className="text-base text-white/70 font-grotesk mt-1">
                                    Duration
                                  </div>
                                </div>
                              </div>

                              {/* Session Tags */}
                              {trainingLog.tag &&
                                trainingLog.tag.length > 0 && (
                                  <div
                                    className={`flex flex-col items-stretch w-full px-4 ${trainingLog.tag.length > 3 ? "gap-1" : "gap-2"}`}
                                  >
                                    {trainingLog.tag.map((tag, index) => (
                                      <span
                                        key={index}
                                        className="flex items-center justify-center px-4 py-2 rounded-md text-sm font-bold font-geist text-white/85 border border-white/30 bg-white/20 truncate w-full min-w-0"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              <img
                                src={overlayLogoUrl}
                                alt="Peak"
                                className="w-28 h-auto"
                              />
                            </div>
                          ) : selectedOverlay === 4 ? (
                            /* Overlay 4: Simple Radial Progress with Peak Readiness */
                            <div className="flex flex-col items-center justify-center space-y-10 scale-[0.75]">
                              {/* Large Radial Progress */}
                              <div className="text-center">
                                <div className="inline-block">
                                  <style>
                                    {`
                                  @import url('https://fonts.googleapis.com/css2?family=Funnel+Sans:wght@300;400;500;600;700&display=swap');

                                  .overlay4-radial-progress {
                                    background: transparent!important;
                                    box-shadow: inset 0 0 0 12px rgba(255, 255, 255, 0.15) !important;
                                    border-color: #ffffff !important;
                                  }
                                  .overlay4-radial-progress::before {
                                    border-color: #ffffff !important;
                                  }
                                  .overlay4-radial-progress span,
                                  .overlay4-radial-progress span * {
                                    color: white !important;
                                    font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
                                    font-weight: 700 !important;
                                  }
                                `}
                                  </style>
                                  <div
                                    style={{
                                      fontFamily:
                                        "Funnel Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
                                    }}
                                  >
                                    <RadialProgress
                                      value={Math.min(
                                        (peakScore / 110) * 100,
                                        100,
                                      )}
                                      size="16rem"
                                      thickness="16px"
                                      color="text-white overlay4-radial-progress"
                                      text={Math.round(peakScore).toString()}
                                      textSize="text-9xl"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Peak Readiness Label */}
                              <div className="text-center">
                                <div className="text-3xl font-semibold font-grotesk text-white">
                                  Peak
                                </div>
                                <div className="text-3xl font-semibold font-grotesk text-white -mt-1">
                                  Readiness
                                </div>
                              </div>
                            </div>
                          ) : selectedOverlay === 3 ? (
                            /* Overlay 3: Grid Layout Preview */
                            <div className="flex flex-col items-center space-y-6 scale-[0.75] w-full pt-6">
                              {/* Grid Layout */}
                              <div className="grid grid-cols-2 gap-y-6 gap-x-6 w-full px-8 pt-2">
                                {/* Row 1: Load and Duration */}
                                <div className="text-center">
                                  <div className="text-5xl font-bold font-grotesk text-white">
                                    {Math.round(trainingLog.load || 0)}
                                  </div>
                                  <div className="text-base text-white/70 font-grotesk mt-1.5">
                                    Load
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-5xl font-bold font-grotesk text-white">
                                    {trainingLog.duration
                                      ? formatMinutes(trainingLog.duration)
                                      : "-"}
                                  </div>
                                  <div className="text-base text-white/70 font-grotesk mt-1.5">
                                    Duration
                                  </div>
                                </div>

                                {/* Row 2: Average HR and Max HR */}
                                {activity && (
                                  <>
                                    <div className="text-center">
                                      <div className="text-5xl font-bold font-grotesk text-white">
                                        {activity.aveHR
                                          ? `${Math.round(activity.aveHR)}`
                                          : "-"}
                                      </div>
                                      <div className="text-base text-white/70 font-grotesk mt-1.5">
                                        Average HR
                                      </div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-5xl font-bold font-grotesk text-white">
                                        {activity.maxHR
                                          ? `${Math.round(activity.maxHR)}`
                                          : "-"}
                                      </div>
                                      <div className="text-base text-white/70 font-grotesk mt-1.5">
                                        Max HR
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* Heart Rate Chart - increased spacing */}
                              {activity && heartRateValues.length > 0 && (
                                <div className="w-full pt-4">
                                  <svg
                                    viewBox={`0 0 ${300} ${80}`}
                                    width={280}
                                    height={75}
                                    className="mx-auto block"
                                  >
                                    <path
                                      d={generateSparklinePath(
                                        heartRateValues,
                                        300,
                                        80,
                                      )}
                                      stroke="#FFFFFF"
                                      strokeWidth={3}
                                      fill="none"
                                    />
                                  </svg>
                                </div>
                              )}
                              <img
                                src={overlayLogoUrl}
                                alt="Peak"
                                className="w-28 h-auto pt-4"
                              />
                            </div>
                          ) : (
                            /* Overlay 1 & 2: Vertical Stack Layout Preview */
                            <div className="flex flex-col items-center space-y-4 scale-[0.75] pt-4">
                              {selectedOverlay === 1 && (
                                <div className="flex flex-col items-center justify-center space-y-4">
                                  <div className="text-center">
                                    <div className="inline-block">
                                      <style>
                                        {`
                                  @import url('https://fonts.googleapis.com/css2?family=Funnel+Sans:wght@300;400;500;600;700&display=swap');

                                  .transparent-radial-progress {
                                    background: transparent!important;
                                    box-shadow: inset 0 0 0 10px rgba(255, 255, 255, 0.2) !important;
                                    border-color: #ffffff !important;
                                  }
                                  .transparent-radial-progress::before {
                                    border-color: #ffffff !important;
                                  }
                                  .transparent-radial-progress span,
                                  .transparent-radial-progress span *,
                                  .radial-progress span,
                                  .radial-progress span *,
                                  span[class*="text-5xl"] {
                                    color: white !important;
                                    font-family: 'Funnel Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
                                    font-weight: 700 !important;
                                  }
                                `}
                                      </style>
                                      <div
                                        style={{
                                          fontFamily:
                                            "Funnel Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
                                        }}
                                      >
                                        <RadialProgress
                                          value={Math.min(
                                            (peakScore / 110) * 100,
                                            100,
                                          )}
                                          size="9rem"
                                          thickness="10px"
                                          color="text-white transparent-radial-progress"
                                          text={Math.round(
                                            peakScore,
                                          ).toString()}
                                          textSize="text-5xl font-grotesk"
                                        />
                                      </div>
                                    </div>
                                    <div className="text-lg text-white/70 font-grotesk mt-2">
                                      Peak Readiness
                                    </div>
                                  </div>
                                </div>
                              )}
                              {/* Load section with increased spacing */}
                              <div className="text-center pt-3">
                                <div className="text-5xl font-bold font-grotesk text-white">
                                  {Math.round(trainingLog.load || 0)}
                                </div>
                                <div className="text-base text-white/70 font-grotesk mt-1.5">
                                  Load
                                </div>
                              </div>
                              {/* Average HR section */}
                              {activity && (
                                <div className="text-center pt-2">
                                  <div className="text-5xl font-bold font-grotesk text-white">
                                    {activity.aveHR
                                      ? `${Math.round(activity.aveHR)}`
                                      : "-"}
                                  </div>
                                  <div className="text-base text-white/70 font-grotesk mt-1.5">
                                    Average HR
                                  </div>
                                </div>
                              )}
                              {/* Max HR section - only show in Overlay 2 */}
                              {selectedOverlay === 2 &&
                                activity &&
                                activity.maxHR && (
                                  <div className="text-center pt-2">
                                    <div className="text-5xl font-bold font-grotesk text-white">
                                      {Math.round(activity.maxHR)}
                                    </div>
                                    <div className="text-base text-white/70 font-grotesk mt-1.5">
                                      Max HR
                                    </div>
                                  </div>
                                )}{" "}
                              {activity && (
                                <div className="w-full pt-6">
                                  {heartRateValues.length > 0 && (
                                    <svg
                                      viewBox={`0 0 ${300} ${80}`}
                                      width={270}
                                      height={75}
                                      className="mx-auto block"
                                    >
                                      <path
                                        d={generateSparklinePath(
                                          heartRateValues,
                                          300,
                                          80,
                                        )}
                                        stroke="#FFFFFF"
                                        strokeWidth={3}
                                        fill="none"
                                      />
                                    </svg>
                                  )}
                                </div>
                              )}
                              <img
                                src={overlayLogoUrl}
                                alt="Peak"
                                className="w-28 h-auto pt-4"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="shrink-0 p-4 pb-6 sm:pb-4 sm:pt-0 mx-4 z-10">
                  <button
                    onClick={handleDownload}
                    disabled={isExporting}
                    className="w-full flex justify-center items-center space-x-2 bg-base-100 text-base-content px-4 py-3 sm:py-4 rounded-full hover:bg-base-100/90 disabled:opacity-50 font-grotesk"
                  >
                    <HiDownload className="w-4 h-4" />
                    <span className="text-sm !font-medium">
                      {isExporting ? "Exporting..." : "Export Overlay"}
                    </span>
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SummaryExportDialog;
